/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const GET_AUTHORIZED_CREATORS_DISCRIMINATOR = 13;

export function getGetAuthorizedCreatorsDiscriminatorBytes() {
  return getU8Encoder().encode(GET_AUTHORIZED_CREATORS_DISCRIMINATOR);
}

export type GetAuthorizedCreatorsInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountProgramAuthority extends string
        ? ReadonlyAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type GetAuthorizedCreatorsInstructionData = { discriminator: number };

export type GetAuthorizedCreatorsInstructionDataArgs = {};

export function getGetAuthorizedCreatorsInstructionDataEncoder(): FixedSizeEncoder<GetAuthorizedCreatorsInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', getU8Encoder()]]),
    (value) => ({
      ...value,
      discriminator: GET_AUTHORIZED_CREATORS_DISCRIMINATOR,
    })
  );
}

export function getGetAuthorizedCreatorsInstructionDataDecoder(): FixedSizeDecoder<GetAuthorizedCreatorsInstructionData> {
  return getStructDecoder([['discriminator', getU8Decoder()]]);
}

export function getGetAuthorizedCreatorsInstructionDataCodec(): FixedSizeCodec<
  GetAuthorizedCreatorsInstructionDataArgs,
  GetAuthorizedCreatorsInstructionData
> {
  return combineCodec(
    getGetAuthorizedCreatorsInstructionDataEncoder(),
    getGetAuthorizedCreatorsInstructionDataDecoder()
  );
}

export type GetAuthorizedCreatorsInput<
  TAccountProgramAuthority extends string = string,
> = {
  /** The program authority PDA */
  programAuthority: Address<TAccountProgramAuthority>;
};

export function getGetAuthorizedCreatorsInstruction<
  TAccountProgramAuthority extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: GetAuthorizedCreatorsInput<TAccountProgramAuthority>,
  config?: { programAddress?: TProgramAddress }
): GetAuthorizedCreatorsInstruction<TProgramAddress, TAccountProgramAuthority> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [getAccountMeta(accounts.programAuthority)],
    data: getGetAuthorizedCreatorsInstructionDataEncoder().encode({}),
    programAddress,
  } as GetAuthorizedCreatorsInstruction<
    TProgramAddress,
    TAccountProgramAuthority
  >);
}

export type ParsedGetAuthorizedCreatorsInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The program authority PDA */
    programAuthority: TAccountMetas[0];
  };
  data: GetAuthorizedCreatorsInstructionData;
};

export function parseGetAuthorizedCreatorsInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedGetAuthorizedCreatorsInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { programAuthority: getNextAccount() },
    data: getGetAuthorizedCreatorsInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
